import { Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router"
import { CalendarBlank, ChatCircle, GearSix, Question, UserList, UsersThree } from "@phosphor-icons/react"
import { useUser } from "@/apis/bff"
import { buttonVariants } from "@/components/ui/button"
import { TooltipContent, TooltipCustom, TooltipPortal, TooltipTrigger } from "@/components/ui/tooltip"
import { normalizeTestId } from "@/lib/test-helper"
import { cn } from "@/lib/utils"
import { ChatBadgeItem } from "@/modules/chats/components/chat-thread/chat-badge-item"
import { getChatRoute } from "@/modules/chats/lib/routing-helpers"
import { SofiaEnvironment } from "@/sofia-environment"
import { default as useCallContextStore } from "@/stores/useCallContextStore"

type NavigationItemWrapperProps = {
  children: React.ReactNode
  title: string
}

const NavigationItemWrapper = ({ children, title }: NavigationItemWrapperProps) => {
  return (
    <TooltipCustom>
      <TooltipTrigger>{children}</TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side="bottom" align="start">
          {title}
        </TooltipContent>
      </TooltipPortal>
    </TooltipCustom>
  )
}

type Navigation = {
  title: string
  icon: React.JSX.Element
  link: string
  id: string
}

const NavigationItems = [
  {
    icon: <CalendarBlank />,
    link: "/",
    title: "common.menu.waitingRoom",
    id: "waiting-room",
  },
  {
    title: "common.menu.chat",
    icon: <ChatCircle />,
    link: getChatRoute(),
    id: "chats",
  },
  {
    title: "common.menu.patients",
    icon: <UserList />,
    link: "/patients",
    id: "patients",
  },
  {
    icon: <UsersThree />,
    link: "/shift-plan-overview",
    title: "common.menu.shiftPlanOverview",
    id: "shift-plan-overview",
  },
]

export const Navigation = () => {
  const { t } = useTranslation()
  const user = useUser()
  // This is temporary will be removed in the future, this is for user story 11420
  const clearStore = useCallContextStore((state) => state.clearStore)

  return (
    <div className="mb-auto inline-grid gap-2">
      {NavigationItems.map(({ id, title, icon, link }) => {
        return (
          <NavigationItemWrapper title={t(title)} key={id}>
            <NavLink
              className={({ isActive }) =>
                cn(
                  buttonVariants({ variant: "circle" }),
                  "relative",
                  isActive && "bg-primary-surface text-on-primary-surface hover:bg-primary-surface-hovered",
                )
              }
              data-testid={normalizeTestId(id, "-nav-link")}
              to={link}
              onClick={() => {
                // This is temporary will be removed in the future, this is for user story 11420
                if (id === "patients") {
                  clearStore()
                }
              }}>
              {icon}
              {id === "chats" && (
                <ErrorBoundary fallback={null}>
                  {/* Use ErrorBoundary for unit tests due to has no server information*/}
                  <Suspense>
                    <ChatBadgeItem />
                  </Suspense>
                </ErrorBoundary>
              )}
            </NavLink>
          </NavigationItemWrapper>
        )
      })}
      <div className="border-border my-2 border-t"></div>
      <NavigationItemWrapper title={t("common.menu.admin")}>
        <NavLink
          className={({ isActive }) =>
            cn(
              buttonVariants({ variant: "circle" }),
              isActive && "bg-primary-surface text-on-primary-surface hover:bg-primary-surface-hovered",
            )
          }
          data-testid="settings-nav-link"
          to="/admin">
          <GearSix />
        </NavLink>
      </NavigationItemWrapper>
      {user.isOneOf(["MedicalSupervisor"]) && (
        <NavigationItemWrapper title={t("common.menu.documentation")}>
          <a
            className={buttonVariants({ variant: "circle" })}
            data-testid={normalizeTestId("documentation", "-nav-link")}
            href={`${SofiaEnvironment.Bff.Url}/api/documentation/`}
            target="_blank"
            rel="noreferrer">
            <Question />
          </a>
        </NavigationItemWrapper>
      )}
    </div>
  )
}
